/**
 * Shared config for Headless UI v2 floating panels (`ComboboxOptions` /
 * `ListboxOptions`).
 *
 * Passing `anchor` makes Headless UI position the panel with Floating UI and render
 * it through a portal at the document root. That fixes two problems the old
 * `absolute ... w-full` panels had:
 *
 *  1. They could not flip, so an input near the bottom of the viewport pushed its
 *     panel off-screen.
 *  2. They could not escape an ancestor `overflow`, so they were clipped by the
 *     scrolling table wrappers (`overflow-x-auto overflow-y-hidden`) and by
 *     `Modal.tsx`'s `overflow-y-auto`. Raising z-index does not help with clipping.
 *
 * Because the panel is portalled it no longer inherits the trigger's width, so the
 * width is read back from the CSS custom property Headless UI sets on the panel:
 * `--input-width` for a Combobox, `--button-width` for a Listbox.
 * `--anchor-max-height` is the space actually available, letting the panel shrink
 * when it is short on room.
 */

/**
 * Spread onto a `ComboboxOptions` / `ListboxOptions`.
 *
 * - `anchor`: `gap` replaces the old `mt-1`; `padding` is the minimum space kept
 *   between the panel and the viewport edge.
 * - `portal`: implied by `anchor`, but stated so the behaviour is obvious at the
 *   call site.
 * - `modal: false`: the default is `true`, which scroll-locks the document and
 *   marks everything else inert while the panel is open. For inline inputs in tables
 *   and forms that is wrong — it freezes page scroll and shifts layout by the
 *   scrollbar width on every open. Floating UI keeps the panel glued to its trigger
 *   via `autoUpdate`, so scrolling stays safe with the lock off.
 * - `transition`: drives the leave fade below via data attributes, replacing the
 *   wrapping `<Transition>` these panels used to need.
 */
export const anchoredPanelProps = {
  anchor: { to: "bottom start", gap: 4, padding: 8 },
  portal: true,
  modal: false,
  transition: true,
} as const;

/**
 * Layout and transition classes shared by every anchored panel. Deliberately no
 * `absolute` or `mt-1` — Floating UI sets position and offset inline. Visual styling
 * (background, ring, shadow, text size) stays with each component, since those
 * legitimately differ.
 */
const sharedPanelClasses = [
  "z-30 overflow-auto focus:outline-none",
  "max-h-[min(15rem,var(--anchor-max-height,15rem))]",
  "data-[leave]:transition data-[leave]:ease-in data-[leave]:duration-100",
  "data-[leave]:data-[closed]:opacity-0",
].join(" ");

/** For `ComboboxOptions` — width comes from the input. */
export const comboboxPanelClasses = `w-[var(--input-width)] ${sharedPanelClasses}`;

/** For `ListboxOptions` — width comes from the button. */
export const listboxPanelClasses = `w-[var(--button-width)] ${sharedPanelClasses}`;
